import {
  isResultCode,
  parseScore,
  type Colour,
  type ResultCode,
  type RoundEntry,
  type Score,
  type Sex,
} from '../model/types.js';
import type {
  TrfAbnormalPoints,
  TrfAcceleration,
  TrfBye,
  TrfDocument,
  TrfForbidden,
  TrfNationalRating,
  TrfPlayer,
  TrfScoreKey,
  TrfTeam,
} from './types.js';

export class TrfParseError extends Error {
  constructor(
    message: string,
    readonly line: number,
  ) {
    super(`TRF line ${line}: ${message}`);
    this.name = 'TrfParseError';
  }
}

/** 0-based column where round 1's block starts in a 001 line. */
export const ROUND_COLUMN = 91;
export const ROUND_WIDTH = 10;

function field(line: string, from1: number, to1: number): string {
  return line.slice(from1 - 1, to1).trim();
}

function parseRoundBlock(block: string, lineNo: number): RoundEntry | null {
  if (block.trim() === '') return null;
  const oppText = block.slice(0, 4).trim();
  const colText = block.charAt(5);
  let resText = block.charAt(7);
  const opp = oppText === '' ? 0 : Number(oppText);
  if (!Number.isInteger(opp) || opp < 0) {
    throw new TrfParseError(`bad opponent "${oppText}"`, lineNo);
  }
  let colour: Colour | null = null;
  if (colText === 'w' || colText === 'W') colour = 'w';
  else if (colText === 'b' || colText === 'B') colour = 'b';
  else if (colText !== '-' && colText !== ' ' && colText !== '') {
    throw new TrfParseError(`bad colour "${colText}"`, lineNo);
  }
  if (resText === '' || resText === ' ') {
    resText = opp > 0 ? '*' : 'Z';
  }
  if (resText === 'u') resText = 'U';
  if (resText === 'h') resText = 'H';
  if (resText === 'f') resText = 'F';
  if (resText === 'z') resText = 'Z';
  if (resText === 'd') resText = 'D';
  if (resText === 'l') resText = 'L';
  if (resText === 'w') resText = 'W';
  if (!isResultCode(resText)) {
    throw new TrfParseError(`bad result "${resText}"`, lineNo);
  }
  let result: ResultCode = resText;
  if (opp === 0) {
    // Without an opponent, '+', '-' and '=' are the old ways of writing byes.
    if (result === '+' || result === '1' || result === 'W') result = 'F';
    else if (result === '-' || result === '0' || result === 'L') result = 'Z';
    else if (result === '=' || result === 'D') result = 'H';
    else if (result === '*') result = 'Z';
    return { opponent: null, colour: null, result };
  }
  return { opponent: opp, colour, result };
}

function parsePlayerLine(line: string, lineNo: number): TrfPlayer {
  const startRank = Number(field(line, 5, 8));
  if (!Number.isInteger(startRank) || startRank <= 0) {
    throw new TrfParseError(`bad starting rank "${field(line, 5, 8)}"`, lineNo);
  }
  const sexText = field(line, 10, 10).toLowerCase();
  const sex: Sex | null = sexText === 'm' ? 'm' : sexText === 'w' || sexText === 'f' ? 'f' : null;
  const ratingText = field(line, 49, 52);
  const rating = ratingText === '' ? 0 : Number(ratingText);
  const pointsText = field(line, 81, 84);
  const rankText = field(line, 86, 89);
  const blocks: (RoundEntry | null)[] = [];
  for (let col = ROUND_COLUMN; col < line.length; col += ROUND_WIDTH) {
    const block = line.slice(col, col + ROUND_WIDTH - 2).padEnd(8, ' ');
    blocks.push(parseRoundBlock(block, lineNo));
  }
  while (blocks.length > 0 && blocks[blocks.length - 1] === null) blocks.pop();
  // A blank block before the last written round means the player was not paired.
  const rounds: RoundEntry[] = blocks.map(
    (b) => b ?? { opponent: null, colour: null, result: 'Z' as const },
  );
  return {
    startRank,
    sex,
    title: field(line, 11, 13),
    name: field(line, 15, 47),
    rating: Number.isFinite(rating) ? rating : 0,
    federation: field(line, 54, 56),
    fideId: field(line, 58, 68),
    birthDate: field(line, 70, 79),
    points: pointsText === '' ? NaN : parseScore(pointsText),
    rank: rankText === '' ? 0 : Number(rankText) || 0,
    rounds,
  };
}

function ids(line: string, from1: number, stride: number, width = 4): number[] {
  const out: number[] = [];
  for (let col = from1 - 1; col < line.length; col += stride) {
    const t = line.slice(col, col + width).trim();
    if (t === '') continue;
    const n = Number(t);
    if (Number.isInteger(n) && n > 0) out.push(n);
  }
  return out;
}

function intField(line: string, from1: number, to1: number, fallback = 0): number {
  const t = field(line, from1, to1);
  if (t === '') return fallback;
  const n = Number(t);
  return Number.isFinite(n) ? n : fallback;
}

function scoreField(line: string, from1: number, to1: number): Score | null {
  const t = field(line, from1, to1);
  if (t === '') return null;
  const v = parseScore(t);
  return Number.isNaN(v) ? null : v;
}

function parseTeam013(line: string): TrfTeam {
  return { name: field(line, 5, 36), members: ids(line, 37, 5) };
}

function parseTeam310(line: string): TrfTeam {
  return {
    pairingNumber: intField(line, 5, 7),
    name: field(line, 9, 40),
    nickname: field(line, 42, 46),
    matchPoints: scoreField(line, 55, 60) ?? 0,
    gamePoints: scoreField(line, 62, 67) ?? 0,
    rank: intField(line, 69, 71),
    members: ids(line, 74, 5),
  };
}

/** XXA NNNN pp.p pp.p ...: id at column 5, round r's points at column 10 + 5(r-1). */
function parseXxa(line: string): TrfAcceleration[] {
  const id = intField(line, 5, 8);
  if (id <= 0) return [];
  const out: TrfAcceleration[] = [];
  for (let r = 1, col = 10; col <= line.length; r++, col += 5) {
    const v = scoreField(line, col, col + 4);
    if (v !== null && v !== 0) {
      out.push({ matchPoints: 0, points: v, firstRound: r, lastRound: r, firstId: id, lastId: id });
    }
  }
  return out;
}

/** TRF26 250: 5-8 match points, 10-13 points, 15-17 first round, 19-21 last, 23-26 first id, 28-31 last id. */
function parse250(line: string): TrfAcceleration {
  return {
    matchPoints: scoreField(line, 5, 8) ?? 0,
    points: scoreField(line, 10, 13) ?? 0,
    firstRound: intField(line, 15, 17),
    lastRound: intField(line, 19, 21),
    firstId: intField(line, 23, 26),
    lastId: intField(line, 28, 31),
  };
}

/** TRF26 162: key at column 6, points at 7-10; further pairs every 9 columns. */
function parse162(line: string, into: Partial<Record<TrfScoreKey, Score>>): void {
  for (let col = 6; col <= line.length; col += 9) {
    const key = line.charAt(col - 1).toUpperCase();
    const v = scoreField(line, col + 1, col + 4);
    if ('WDLAPX'.includes(key) && key !== '' && v !== null) into[key as TrfScoreKey] = v;
  }
}

const XXS_MAP: Record<string, TrfScoreKey> = {
  W: 'W',
  WW: 'W',
  BW: 'W',
  FW: 'W',
  FPB: 'W',
  D: 'D',
  WD: 'D',
  BD: 'D',
  HPB: 'D',
  WL: 'L',
  BL: 'L',
  L: 'L',
  ZPB: 'A',
  FL: 'A',
  PAB: 'P',
};

function parseXxs(rest: string, into: Partial<Record<TrfScoreKey, Score>>): void {
  for (const tok of rest.trim().split(/\s+/)) {
    const [k, v] = tok.split('=');
    if (k === undefined || v === undefined) continue;
    const key = XXS_MAP[k.toUpperCase()];
    const val = parseScore(v);
    if (key !== undefined && !Number.isNaN(val)) into[key] = val;
  }
}

const BB_MAP: Record<string, TrfScoreKey> = { W: 'W', D: 'D', L: 'L', Z: 'A', F: 'A', U: 'P' };

function parseNrs(line: string): TrfNationalRating {
  return {
    federation: line.slice(0, 3),
    startRank: intField(line, 5, 8),
    sex: field(line, 10, 10),
    classification: field(line, 11, 13),
    name: field(line, 15, 47),
    rating: intField(line, 49, 52),
    origin: field(line, 54, 56),
    nationalId: field(line, 58, 68),
    birthDate: field(line, 70, 79),
  };
}

function splitList(rest: string): string[] {
  return rest
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '');
}

/**
 * Parse TRF text: TRF-16, TRF26, and the legacy TRF(x)/TRF(bx) extension
 * lines (XXR, XXC, XXA, XXP, XXS, XXZ, BB*) used by JaVaFo and bbpPairings.
 */
export function parseTrf(text: string): TrfDocument {
  const headers = new Map<string, string>();
  const players: TrfPlayer[] = [];
  const teams: TrfTeam[] = [];
  const accelerations: TrfAcceleration[] = [];
  const otherLines: string[] = [];
  const absentNextRound: number[] = [];
  const scoring: Partial<Record<TrfScoreKey, Score>> = {};
  const byes: TrfBye[] = [];
  const forbidden: TrfForbidden[] = [];
  const abnormalPoints: TrfAbnormalPoints[] = [];
  const nationalRatings: TrfNationalRating[] = [];
  let tiebreaks: string[] = [];
  let standings: string[] = [];
  let tournamentType: string | null = null;
  let totalRounds: number | null = null;
  let initialColour: 'w' | 'b' | null = null;
  let useRankAsPairingId = false;

  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/);
  lines.forEach((raw, i) => {
    const lineNo = i + 1;
    const line = raw.replace(/\s+$/, '');
    if (line.trim() === '' || line.startsWith('###')) return;
    const code = line.slice(0, 3);
    const rest = line.slice(3);
    switch (code) {
      case '001':
        players.push(parsePlayerLine(line, lineNo));
        return;
      case '013':
        teams.push(parseTeam013(line));
        return;
      case '310':
        teams.push(parseTeam310(line));
        return;
      case 'XXR':
      case '142': {
        const n = Number(rest.trim().split(/\s+/)[0]);
        if (Number.isInteger(n) && n > 0) totalRounds = n;
        return;
      }
      case 'XXC': {
        for (const tok of rest.trim().toLowerCase().split(/\s+/)) {
          if (tok === 'white1') initialColour = 'w';
          else if (tok === 'black1') initialColour = 'b';
          else if (tok === 'rank') useRankAsPairingId = true;
        }
        return;
      }
      case '152': {
        const c = rest.trim().toUpperCase();
        if (c === 'W') initialColour = 'w';
        else if (c === 'B') initialColour = 'b';
        return;
      }
      case '162':
        parse162(line, scoring);
        return;
      case 'XXS':
        parseXxs(rest, scoring);
        return;
      case 'BBW':
      case 'BBD':
      case 'BBL':
      case 'BBZ':
      case 'BBF':
      case 'BBU': {
        const v = parseScore(rest);
        const key = BB_MAP[code.charAt(2)];
        if (!Number.isNaN(v) && key !== undefined) scoring[key] = v;
        return;
      }
      case '192':
        tournamentType = rest.trim() || null;
        return;
      case '202':
        tiebreaks = splitList(rest);
        return;
      case '212':
        standings = splitList(rest);
        return;
      case 'XXA':
        accelerations.push(...parseXxa(line));
        return;
      case '250':
        accelerations.push(parse250(line));
        return;
      case '240': {
        const k = line.charAt(4).toUpperCase();
        byes.push({
          kind: k === 'F' || k === 'H' || k === 'Z' ? k : null,
          round: intField(line, 7, 9),
          ids: ids(line, 11, 5),
        });
        return;
      }
      case '260':
        forbidden.push({
          firstRound: intField(line, 5, 7, 1),
          lastRound: intField(line, 9, 11, 9999),
          ids: ids(line, 13, 5),
        });
        return;
      case 'XXP': {
        const list = rest
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isInteger(n) && n > 0);
        forbidden.push({ firstRound: 1, lastRound: 9999, ids: list });
        return;
      }
      case '299':
        abnormalPoints.push({
          type: field(line, 5, 5),
          matchPoints: scoreField(line, 8, 11),
          points: scoreField(line, 14, 17),
          round: intField(line, 20, 22),
          ids: ids(line, 24, 5),
        });
        return;
      case 'XXZ': {
        for (const tok of rest.trim().split(/\s+/)) {
          const n = Number(tok);
          if (Number.isInteger(n) && n > 0) absentNextRound.push(n);
        }
        return;
      }
      default:
        if (/^[A-Z]{3}$/.test(code) && /^ [ \d]{3}\d/.test(rest)) {
          nationalRatings.push(parseNrs(line));
        } else if (/^\d{3}$/.test(code)) {
          if (!headers.has(code)) headers.set(code, rest.replace(/^ /, ''));
          else otherLines.push(line);
        } else {
          otherLines.push(line);
        }
    }
  });

  return {
    headers,
    players,
    teams,
    totalRounds,
    initialColour,
    useRankAsPairingId,
    accelerations,
    scoring,
    tournamentType,
    tiebreaks,
    standings,
    byes,
    forbidden,
    abnormalPoints,
    nationalRatings,
    absentNextRound,
    otherLines,
  };
}
